/**
 * 録音同意ダイアログコンポーネント
 * 面接録音を開始する前に同意を取得するためのダイアログ
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import SecurityIcon from '@mui/icons-material/Security';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import { bg, border, text as textColors } from '../../../../utils/darkModeAdapter';

const ConsentDialog = ({
  open,
  onClose,
  onConsent,
  loading = false,
  error = null,
}) => {
  const theme = useTheme();
  const [agreed, setAgreed] = useState(false);

  const handleConsent = () => {
    if (agreed && onConsent) {
      onConsent();
    }
  };

  const handleClose = () => {
    setAgreed(false);
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${border(theme).light}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 600,
          color: textColors(theme).primary,
          pb: 1,
        }}
      >
        <MicIcon sx={{ color: '#249BE0' }} />
        面接録音の同意確認
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: textColors(theme).secondary, mb: 3, lineHeight: 1.8 }}
        >
          面接の音声を録音し、AI解析を行います。録音を開始する前に、以下の内容をご確認ください。
        </Typography>

        {/* 説明項目 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <SecurityIcon sx={{ color: '#249BE0', mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                データの取り扱い
              </Typography>
              <Typography variant="body2" sx={{ color: textColors(theme).secondary }}>
                録音データは暗号化して安全に保存され、面接評価の目的にのみ使用されます。
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <DeleteOutlineIcon sx={{ color: '#249BE0', mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                自動削除
              </Typography>
              <Typography variant="body2" sx={{ color: textColors(theme).secondary }}>
                録音データは90日後に自動的に削除されます。手動での削除も可能です。
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                候補者への説明
              </Typography>
              <Typography variant="body2" sx={{ color: textColors(theme).secondary }}>
                録音を行う場合は、面接開始前に候補者に録音の旨を説明し、同意を得てください。
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* 同意チェックボックス */}
        <Box
          sx={{
            backgroundColor: bg(theme).subtle,
            borderRadius: 2,
            p: 2,
            border: `1px solid ${border(theme).light}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{
                  color: textColors(theme).muted,
                  '&.Mui-checked': {
                    color: '#249BE0',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: textColors(theme).primary }}>
                上記の内容を理解し、録音に同意します。候補者にも録音について説明し、同意を得ました。
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: textColors(theme).secondary,
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            '&:hover': {
              backgroundColor: bg(theme).hover,
            },
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleConsent}
          variant="contained"
          disabled={!agreed || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <MicIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#249BE0',
            '&:hover': {
              backgroundColor: '#1976d2',
            },
            '&:disabled': {
              backgroundColor: '#94a3b8',
            },
          }}
        >
          {loading ? '処理中...' : '同意して録音開始'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentDialog;
